import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    MdDashboard, MdSchool, MdSupervisedUserCircle, MdCurrencyRupee,
    MdSettings, MdLogout, MdMenu, MdClose, MdSearch, MdCheckCircle, MdVisibility,
    MdFileDownload, MdFilterList, MdChevronLeft, MdChevronRight, MdDateRange, MdAdd, MdDelete, MdEdit,
    MdNotifications, MdPerson, MdAssignment, MdPendingActions, MdTrendingUp, MdLocationOn,
    MdEmail, MdPhone, MdEvent, MdCreditCard, MdFingerprint, MdVerified, MdInfo, MdGroupWork, MdInventory, MdCameraAlt, MdWarning, MdHardware, MdVerifiedUser, MdRestore,
    MdLocationCity, MdCalendarMonth, MdTrackChanges
} from 'react-icons/md';
import { Truck } from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import logo from '../assets/logo.png';
// import AdminSystemSettings from '../components/AdminSystemSettings'; // Pending creation
// Heavy tab components — loaded lazily (only when tab is first opened)
const AdminInspectionGallery = React.lazy(() => import('../components/AdminInspectionGallery'));
const InventoryManager = React.lazy(() => import('../components/InventoryManager'));
const AdminPenaltyReport = React.lazy(() => import('../components/AdminPenaltyReport'));
const AdminNoticeManager = React.lazy(() => import('../components/AdminNoticeManager'));
const AdminHardwareReports = React.lazy(() => import('../components/AdminHardwareReports'));
const KushalYuvaAdminView = React.lazy(() => import('../components/KushalYuvaAdminView'));
const AdminAutoManagement = React.lazy(() => import('../components/AdminAutoManagement'));
const AdminTeacherManager = React.lazy(() => import('../components/AdminTeacherManager'));
const AdminCenterFacilities = React.lazy(() => import('../components/AdminCenterFacilities'));
const AdminGovtTracker = React.lazy(() => import('../components/AdminGovtTracker'));
const AdminFreeAdmissions = React.lazy(() => import('../components/AdminFreeAdmissions'));
const AdminHalfAdmissions = React.lazy(() => import('../components/AdminHalfAdmissions'));
const AdminDrccApprovals = React.lazy(() => import('../components/AdminDrccApprovals'));
const AdminTargetManagement = React.lazy(() => import('../components/AdminTargetManagement'));



export default function AdminDashboard() {
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 900);
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 900);
    const [activeTab, setActiveTab] = useState(() => {
        return localStorage.getItem('adminActiveTab') || 'dashboard';
    });
    const { user, logout } = useAuth();
    const isCollapsed = isDesktop && !isSidebarOpen;

    useEffect(() => {
        localStorage.setItem('adminActiveTab', activeTab);
    }, [activeTab]);

    // Data States
    const [admissions, setAdmissions] = useState([]);
    const [users, setUsers] = useState([]); // User Management
    const [selectedStudent, setSelectedStudent] = useState(null);

    // Notifications State
    const [notifications, setNotifications] = useState([]);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);

    // User Modal State
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [allConductors, setAllConductors] = useState([]); // True list of conductors regardless of current filters
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalStudents: 0,
        totalRevenue: 0,
        todayAdmissions: 0,
        pendingAdmissions: 0,
        totalPenalties: 0
    });

    // Filter & Search States
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        center: '',
        startDate: '',
        endDate: '',
        conductedBy: '',
        paymentStatus: 'paid', // Default to PAID ONLY to keep view clean
        filterMonthYear: ''
    });

    // Server-side pagination state
    const [serverPage, setServerPage] = useState(1);
    const [serverTotal, setServerTotal] = useState(0);
    const [serverLastPage, setServerLastPage] = useState(1);
    const itemsPerPage = 50;

    const [isExporting, setIsExporting] = useState(false);

    // Performance Page Filters
    const [perfFilterCenter, setPerfFilterCenter] = useState('');
    const [perfFilterMonth, setPerfFilterMonth] = useState('');


    // Ref for detecting clicks outside
    const notifRef = useRef(null);
    const profileRef = useRef(null);
    const fetchAbortRef = useRef({ _reqId: 0 }); // Request counter to ignore stale responses

    // State ref to keep polling updated without reseting closures
    const pollDataRef = useRef({});
    useEffect(() => {
        pollDataRef.current = { fetchAdmissions, serverPage, filters, debouncedSearchQuery };
    });

    const fetchNotifications = async () => {
        try {
            const res = await api.get('/notifications');
            setNotifications(res.data);
        } catch (error) {
            console.error("Failed to fetch notifications");
        }
    };

    const handleNotificationClick = (n) => {
        if (!n.read_at) {
            markAsRead(n.id);
        }

        if (n.data.admission_id) {
            setActiveTab('students');
            const student = admissions.find(s => s.id === n.data.admission_id);
            if (student) setSelectedStudent(student);
        }

        setShowNotifDropdown(false);
    };

    const markAsRead = async (id) => {
        try {
            await api.post(`/notifications/${id}/read`);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (error) { }
    };

    const markAllRead = async () => {
        try {
            await api.post('/notifications/read-all');
            setNotifications([]);
        } catch (error) { }
    }
    const fetchAdmissions = async (page = 1, currentFilters = filters, searchVal = debouncedSearchQuery) => {
        // Use a simple counter to ignore stale responses
        const reqId = ++fetchAbortRef.current._reqId;

        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('status', currentFilters.paymentStatus === 'pending' ? 'pending' : currentFilters.paymentStatus === 'all' ? 'all' : 'paid');
            params.set('per_page', itemsPerPage);
            params.set('page', page);

            if (searchVal) params.set('search', searchVal);
            if (currentFilters.conductedBy) params.set('teacher_id', currentFilters.conductedBy);
            if (currentFilters.filterMonthYear) {
                const [fm, fy] = currentFilters.filterMonthYear.split('-');
                params.set('month', fm);
                params.set('year', fy);
            }

            const res = await api.get(`/admissions?${params.toString()}`);

            // Only update state if this is still the latest request
            if (reqId !== fetchAbortRef.current._reqId) return;

            const resData = res.data.admissions;
            const dataArr = Array.isArray(resData) ? resData : (resData?.data || []);

            setAdmissions(dataArr);
            setServerTotal(resData?.total || dataArr.length);
            setServerLastPage(resData?.last_page || 1);
            setServerPage(page);

            const penRes = await api.get('/penalties/all');
            const totalPenSum = Array.isArray(penRes.data) ? penRes.data.reduce((sum, p) => sum + (p.total_fine || 0), 0) : 0;

            setStats({
                totalStudents: res.data.total_students,
                pendingAdmissions: res.data.pending_admissions,
                totalRevenue: res.data.total_revenue,
                todayAdmissions: res.data.today_admissions,
                totalPenalties: totalPenSum
            });
        } catch (error) {
            console.error('Failed to fetch admissions:', error);
        } finally {
            if (reqId === fetchAbortRef.current._reqId) {
                setLoading(false);
            }
        }
    };


    const fetchUsers = async () => {
        try {
            const res = await api.get('/users');
            setUsers(res.data);
        } catch (error) {
            console.error("Failed to fetch users");
        }
    }

    const handleDeleteUser = async (user) => {
        if (window.confirm(`Are you sure you want to delete user "${user.name}"?\n\nTheir data (admissions) will remain safe with Admin, but the user will be logged out immediately and cannot access the system anymore.`)) {
            try {
                await api.delete(`/users/${user.id}`);
                fetchUsers();
            } catch (error) {
                alert("Failed to delete user.");
            }
        }
    }

    const openAddUser = () => {
        setEditingUser(null);
        setIsUserModalOpen(true);
    }

    const openEditUser = (user) => {
        setEditingUser(user);
        setIsUserModalOpen(true);
        setShowProfileMenu(false);
    }

    // Debounce search query — 600ms so backend gets clean single request
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 600);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Re-fetch whenever filters or debounced search changes — always reset to page 1
    useEffect(() => {
        fetchAdmissions(1, filters, debouncedSearchQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, debouncedSearchQuery]);

    const handleUserModalSuccess = async (isSelfUpdate) => {
        await fetchUsers();
        if (isSelfUpdate) {
            window.location.reload();
        }
    }


    useEffect(() => {
        const handleResize = () => {
            const desktop = window.innerWidth > 900;
            setIsDesktop(desktop);
            if (!desktop) {
                setIsSidebarOpen(false);
            }
        };
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) setShowNotifDropdown(false);
            if (profileRef.current && !profileRef.current.contains(event.target)) setShowProfileMenu(false);
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);

        fetchAdmissions();
        fetchUsers();
        fetchNotifications();

        // Polling for notifications every 30s (was 10s)
        const notifInterval = setInterval(fetchNotifications, 30000);

        // Admissions Polling — every 60s only (was every 5s — was killing performance!)
        const admissionsInterval = setInterval(() => {
            if (pollDataRef.current?.fetchAdmissions) {
                const { fetchAdmissions, serverPage, filters, debouncedSearchQuery } = pollDataRef.current;
                fetchAdmissions(serverPage, filters, debouncedSearchQuery);
            }
        }, 60000);

        // Background fetch to get ALL conductors who have admissions (bypass pagination/filtering constraints)
        const fetchAllConductors = async () => {
            try {
                const res = await api.get('/admissions?per_page=999999&status=all');
                const dataArr = Array.isArray(res.data.admissions) ? res.data.admissions : (res.data.admissions?.data || []);
                const unique = [];
                const seenIds = new Set();
                dataArr.forEach(adm => {
                    if (adm.teacher_id && !seenIds.has(adm.teacher_id)) {
                        seenIds.add(adm.teacher_id);
                        unique.push({ id: adm.teacher_id, name: adm.conducted_by || 'Unknown' });
                    }
                });
                setAllConductors(unique.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (e) {
                console.error("Failed to fetch all conductors for dropdown.");
            }
        };
        fetchAllConductors();

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
            clearInterval(notifInterval);
            clearInterval(admissionsInterval);
        };
    }, []);

    // Also watch currently fetched admissions to instantly add any brand new conductors that just took an admission
    useEffect(() => {
        setAllConductors(prev => {
            let updated = false;
            const unique = [...prev];
            const seenIds = new Set(prev.map(c => c.id));
            (admissions || []).forEach(adm => {
                if (adm.teacher_id && !seenIds.has(adm.teacher_id)) {
                    seenIds.add(adm.teacher_id);
                    unique.push({ id: adm.teacher_id, name: adm.conducted_by || 'Unknown' });
                    updated = true;
                }
            });
            return updated ? unique.sort((a, b) => a.name.localeCompare(b.name)) : prev;
        });
    }, [admissions]);

    // --- Filtering Logic ---
    const uniqueCenters = useMemo(() => [...new Set((admissions || []).map(s => s.center_name))].filter(Boolean), [admissions]);

    // --- Performance Stats Logic ---
    const performanceStats = useMemo(() => {
        const stats = {};
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        admissions.forEach(adm => {
            // ONLY count admin-approved (paid) admissions in performance table
            if (adm.payment_status !== 'paid') return;

            const byId = adm.teacher_id || 'unknown';
            const by = (adm.conducted_by || 'Unknown').trim();
            const center = (adm.center_name || 'Unknown').trim();
            const key = `${center}-${byId}`;

            if (!stats[key]) {
                const teacherUser = users.find(u => u.id === adm.teacher_id);
                const centerUser = users.find(u =>
                    u.center_name?.trim().toLowerCase() === center.toLowerCase()
                );

                stats[key] = {
                    id: key,
                    center_name: center,
                    teacher_id: byId,
                    conducted_by: by, // We keep the latest name representation
                    count: 0,
                    today_count: 0,
                    monthly_count: 0,
                    commission_rate: teacherUser?.commission_per_admission || 0,
                    center_logo_url: centerUser?.center_logo_url,
                    recent_student: adm.student_name,
                    monthly_admissions: {} // month-year -> count
                };
            }

            stats[key].count += 1;

            if (adm.admission_date || adm.created_at) {
                const admDate = new Date(adm.admission_date || adm.created_at);
                const admDateStr = admDate.toISOString().split('T')[0];
                const mKey = `${admDate.getMonth() + 1}-${admDate.getFullYear()}`;
                stats[key].monthly_admissions[mKey] = (stats[key].monthly_admissions[mKey] || 0) + 1;

                if (admDateStr === todayStr) stats[key].today_count += 1;
                if (admDate.getMonth() === currentMonth && admDate.getFullYear() === currentYear) {
                    stats[key].monthly_count += 1;
                }
            }
        });

        return Object.values(stats).map(s => ({
            ...s,
            today_earning: s.today_count * s.commission_rate,
            monthly_earning: s.monthly_count * s.commission_rate
        })).sort((a, b) => b.count - a.count);
    }, [admissions, users]);

    // --- Performance Filter: unique centers ---
    const perfCentersList = useMemo(() => [...new Set(performanceStats.map(s => s.center_name))].sort(), [performanceStats]);

    // Month list for last 18 months
    const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const perfMonthOptions = useMemo(() => {
        const opts = [];
        const now = new Date();
        for (let i = 0; i < 18; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            opts.push({ label: `${MONTH_NAMES[d.getMonth() + 1]} ${d.getFullYear()}`, value: `${d.getMonth() + 1}-${d.getFullYear()}` });
        }
        return opts;
    }, []);

    // --- Filtered Performance Stats ---
    const filteredPerfStats = useMemo(() => {
        return performanceStats
            .filter(s => !perfFilterCenter || s.center_name === perfFilterCenter)
            .map(s => {
                if (!perfFilterMonth) return s;
                const monthCount = s.monthly_admissions[perfFilterMonth] || 0;
                return {
                    ...s,
                    count: monthCount,
                    today_count: 0,
                    monthly_count: monthCount,
                    today_earning: 0,
                    monthly_earning: monthCount * s.commission_rate
                };
            })
            .filter(s => s.count > 0 || !perfFilterMonth)
            .sort((a, b) => b.count - a.count);
    }, [performanceStats, perfFilterCenter, perfFilterMonth]);

    // filteredStudents still used for export and performance stats
    const filteredStudents = useMemo(() => {
        return admissions.filter(student => {
            const matchesStatus =
                filters.paymentStatus === 'paid' ? student.payment_status === 'paid' :
                    filters.paymentStatus === 'pending' ? student.payment_status === 'pending' : true;
            return matchesStatus;
        });
    }, [admissions, filters]);

    // Server-side pagination handlers
    const totalPages = serverLastPage;
    const paginatedStudents = admissions; // Already paginated by server

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        fetchAdmissions(page, filters, debouncedSearchQuery);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            // Build query params identical to fetchAdmissions but with large per_page
            const params = new URLSearchParams();
            params.set('status', filters.paymentStatus === 'pending' ? 'pending' : filters.paymentStatus === 'all' ? 'all' : 'paid');
            params.set('per_page', 999999); // Get all data
            params.set('page', 1);

            if (debouncedSearchQuery) params.set('search', debouncedSearchQuery);
            if (filters.conductedBy) params.set('teacher_id', filters.conductedBy);
            if (filters.filterMonthYear) {
                const [fm, fy] = filters.filterMonthYear.split('-');
                params.set('month', fm);
                params.set('year', fy);
            }

            const res = await api.get(`/admissions?${params.toString()}`);
            const resData = res.data.admissions;
            const dataArr = Array.isArray(resData) ? resData : (resData?.data || []);

            // Further filter by paymentStatus if needed (already handled by 'status' param mostly, but just in case)
            const exportData = dataArr.filter(student => {
                const matchesStatus =
                    filters.paymentStatus === 'paid' ? student.payment_status === 'paid' :
                        filters.paymentStatus === 'pending' ? student.payment_status === 'pending' : true;
                return matchesStatus;
            });

            const headers = ["Student Name", "Reg No", "DRCSS No", "Course", "Batch", "Amount", "Date", "Center", "Phone", "Aadhar No"];

            // Build CSV properly handling commas in names etc.
            const csvRows = exportData.map(e => [
                `"${(e.student_name || '').replace(/"/g, '""')}"`,
                e.registration_no || "Pending",
                `"${(e.drcss_no || '').replace(/"/g, '""')}"`,
                `"${(e.course || '').replace(/"/g, '""')}"`,
                `"${(e.batch || '').replace(/"/g, '""')}"`,
                e.payment_amount || 0,
                e.admission_date || e.created_at,
                `"${(e.center_name || '').replace(/"/g, '""')}"`,
                `"${(e.phone || '').replace(/"/g, '""')}"`,
                `"${(e.aadhar_no || '').replace(/"/g, '""')}"`
            ].join(","));

            const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + csvRows.join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `students_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Export failed", error);
            alert("Failed to export data. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const closeSidebarMobile = () => {
        if (window.innerWidth <= 900) setIsSidebarOpen(false);
    }

    return (
        <div style={{ display: 'flex', height: '100vh', background: '#F0F2F5', fontFamily: 'var(--font-family-base)', overflow: 'hidden' }}>

            {!isDesktop && isSidebarOpen && (
                <div className="mobile-overlay" onClick={() => setIsSidebarOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 45 }} />
            )}

            <motion.aside initial={false} animate={{ width: isDesktop ? (isSidebarOpen ? '220px' : '64px') : '220px', x: isDesktop ? 0 : (isSidebarOpen ? 0 : -220) }} transition={{ duration: 0.3 }}
                style={{ background: '#0f172a', height: '100%', position: 'fixed', zIndex: 50, left: 0, top: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #1e293b' }}>

                <div style={{
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    padding: isCollapsed ? '0' : '0 1.2rem',
                    background: 'rgba(255,255,255,0.03)',
                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isCollapsed ? '0' : '0.8rem' }}>
                        <div style={{
                            background: 'white',
                            padding: '4px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            <img src={logo} alt="Logo" style={{ height: '32px', width: '36px', objectFit: 'contain' }} />
                        </div>
                        {!isCollapsed && (
                            <div>
                                <span style={{ display: 'block', fontWeight: '800', fontSize: '0.95rem', color: 'white', letterSpacing: '0.5px', lineHeight: '1.2' }}>SECURE CMS</span>
                                <span style={{ display: 'block', fontSize: '0.65rem', color: '#818cf8', fontWeight: '700', letterSpacing: '1.5px' }}>ADMIN PANEL</span>
                            </div>
                        )}
                    </div>
                </div>

                <nav className="no-scrollbar" style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
                    {/* Sub Admin badge */}
                    {user?.role === 'sub_admin' && (
                        isCollapsed ? (
                            <div style={{ display: 'flex', justifyContent: 'center', margin: '0.5rem 0', color: 'white', fontSize: '1.1rem' }} title="SUB ADMIN">
                                🔐
                            </div>
                        ) : (
                            <div style={{ padding: '0.4rem 0.8rem', background: '#6B46C1', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700', color: 'white', textAlign: 'center', marginBottom: '0.5rem', letterSpacing: '0.5px' }}>
                                🔐 SUB ADMIN — Limited Access
                            </div>
                        )
                    )}
                    {/* Dashboard - always visible */}
                    <NavItem icon={<MdDashboard />} label="Dashboard Overview" isActive={activeTab === 'dashboard'} onClick={() => { setActiveTab('dashboard'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />

                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('admissions'))) && (
                        <NavItem icon={<MdSchool />} label="All Admissions" isActive={activeTab === 'students'} onClick={() => { setActiveTab('students'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('admissions'))) && (
                        <NavItem icon={<MdCurrencyRupee />} label="Free Admissions" isActive={activeTab === 'free_admissions'} onClick={() => { setActiveTab('free_admissions'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('admissions'))) && (
                        <NavItem icon={<MdCurrencyRupee />} label="Half Admissions (500)" isActive={activeTab === 'half_admissions'} onClick={() => { setActiveTab('half_admissions'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('kyp'))) && (
                        <NavItem icon={<MdAssignment />} label="All Registrations" isActive={activeTab === 'kyp_apps'} onClick={() => { setActiveTab('kyp_apps'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('admissions'))) && (
                        <NavItem icon={<MdAssignment />} label="DRCC Approvals" isActive={activeTab === 'drcc_approvals'} onClick={() => { setActiveTab('drcc_approvals'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {/* Merged into Target Sheet
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('performance'))) && (
                        <NavItem icon={<MdGroupWork />} label="Performance" isActive={activeTab === 'performance'} onClick={() => { setActiveTab('performance'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )} 
                    */}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('performance'))) && (
                        <NavItem icon={<MdTrackChanges />} label="Admission Target" isActive={activeTab === 'target'} onClick={() => { setActiveTab('target'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {/* Govt Submission Tracker — admin only, placed under admissions section */}
                    {user?.role === 'admin' && (
                        <NavItem icon={<MdVerified />} label="Govt Submission" isActive={activeTab === 'govt_tracker'} onClick={() => { setActiveTab('govt_tracker'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('inspections'))) && (
                        <NavItem icon={<MdCameraAlt />} label="Inspection Gallery" isActive={activeTab === 'inspections'} onClick={() => { setActiveTab('inspections'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('hardware'))) && (
                        <NavItem icon={<MdHardware />} label="Hardware & Visits" isActive={activeTab === 'hardware'} onClick={() => { setActiveTab('hardware'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('inventory'))) && (
                        <NavItem icon={<MdInventory />} label="Inventory & Assets" isActive={activeTab === 'inventory'} onClick={() => { setActiveTab('inventory'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('centers'))) && (
                        <NavItem icon={<MdCheckCircle />} label="Center Assets" isActive={activeTab === 'facilities'} onClick={() => { setActiveTab('facilities'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('monitoring'))) && (
                        <NavItem icon={<Truck size={20} />} label="Auto Management" isActive={activeTab === 'autos'} onClick={() => { setActiveTab('autos'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('notices'))) && (
                        <NavItem icon={<MdNotifications />} label="Manage Notices" isActive={activeTab === 'notices'} onClick={() => { setActiveTab('notices'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('users'))) && (
                        <NavItem icon={<MdSupervisedUserCircle />} label="User Management" isActive={activeTab === 'users'} onClick={() => { setActiveTab('users'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                    {(user?.role === 'admin' || (user?.permissions && user.permissions.includes('users'))) && (
                        <NavItem icon={<MdGroupWork />} label="Teacher Management" isActive={activeTab === 'teachers'} onClick={() => { setActiveTab('teachers'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}

                    {/* Settings only for full admin */}
                    {user?.role === 'admin' && (
                        <NavItem icon={<MdSettings />} label="System Settings" isActive={activeTab === 'settings'} onClick={() => { setActiveTab('settings'); closeSidebarMobile(); }} isCollapsed={isCollapsed} />
                    )}
                </nav>

                <div style={{ padding: isCollapsed ? '1rem 0' : '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'center' }}>
                    <button onClick={logout} style={{
                        width: isCollapsed ? '40px' : '100%',
                        height: isCollapsed ? '40px' : 'auto',
                        padding: isCollapsed ? '0' : '0.8rem 1.2rem',
                        background: '#C53030',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: isCollapsed ? '0' : '0.6rem',
                        fontSize: isCollapsed ? '1.2rem' : '0.85rem'
                    }} title="Sign Out">
                        <MdLogout /> {!isCollapsed && "Sign Out"}
                    </button>
                </div>
            </motion.aside>

            <div className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflowY: 'auto', marginLeft: isDesktop ? (isSidebarOpen ? '220px' : '64px') : '0px', transition: 'margin-left 0.3s', position: 'relative' }}>
                <header style={{
                    height: '64px',
                    minHeight: '64px',
                    maxHeight: '64px',
                    flexShrink: 0,
                    padding: '0 1rem',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 200,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #E2E8F0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div onClick={toggleSidebar} style={{ cursor: 'pointer', padding: '0.5rem', borderRadius: '8px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} className="hover:bg-blue-50">
                            <MdMenu size={24} color="#3182CE" />
                        </div>
                        <h2 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            Welcome Back, {user?.name || 'Admin'} 👋
                        </h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        <div style={{ position: 'relative' }} ref={notifRef}>
                            <div onClick={() => setShowNotifDropdown(!showNotifDropdown)} style={{ cursor: 'pointer', position: 'relative' }}>
                                <MdNotifications size={26} color="#718096" />
                                {notifications.length > 0 && (
                                    <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)', color: 'white', borderRadius: '50%', fontSize: '0.65rem', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)', border: '2px solid white' }}>{notifications.length}</span>
                                )}
                            </div>
                            <AnimatePresence>
                                {showNotifDropdown && (
                                    <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} style={{ position: 'absolute', top: '120%', right: '-10px', width: '320px', background: 'white', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #E2E8F0', zIndex: 100, overflow: 'hidden' }}>
                                        <div style={{ padding: '0.8rem', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#2D3748' }}>Notifications</h4>
                                            {notifications.length > 0 && <span onClick={markAllRead} style={{ fontSize: '0.75rem', color: '#3182CE', cursor: 'pointer' }}>Mark all read</span>}
                                        </div>
                                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {notifications.length === 0 ? <div style={{ padding: '1.5rem', textAlign: 'center', color: '#A0AEC0', fontSize: '0.9rem' }}>No new notifications</div> : notifications.map(n => (
                                                <div key={n.id} style={{ padding: '0.8rem', borderBottom: '1px solid #F7FAFC', cursor: 'pointer', background: !n.read_at ? '#F0FFF4' : 'white' }} onClick={() => handleNotificationClick(n)}>
                                                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#2D3748' }}>{n.data.message}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '2px' }}><span style={{ fontWeight: 'bold' }}>{n.data.center_name}</span> • {new Date(n.created_at).toLocaleTimeString()}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <div style={{ position: 'relative' }} ref={profileRef}>
                            <div onClick={() => setShowProfileMenu(!showProfileMenu)} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer' }}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #3182CE 0%, #2c5282 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.9rem', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden', transition: 'transform 0.2s', flexShrink: 0 }}>
                                    {(user?.center_logo_url || user?.profile_photo_url) ?
                                        <img src={user.center_logo_url || user.profile_photo_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : user?.name?.charAt(0).toUpperCase()
                                    }
                                </div>
                                <div style={{ textAlign: 'left' }} className="hidden sm:block">
                                    <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#111827', lineHeight: '1.1' }}>{user?.name || 'Admin'}</div>
                                    <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#6B7280', textTransform: 'capitalize', marginTop: '1px' }}>Administrator</div>
                                </div>
                            </div>
                            <AnimatePresence>
                                {showProfileMenu && (
                                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ position: 'absolute', top: '120%', right: '0', width: '200px', background: 'white', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', border: '1px solid #E2E8F0', zIndex: 100, overflow: 'hidden' }}>
                                        <div style={{ padding: '1rem', borderBottom: '1px solid #E2E8F0', background: '#F7FAFC' }}>
                                            <div style={{ fontWeight: '700', color: '#2D3748', fontSize: '0.9rem' }}>{user?.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#718096' }}>{user?.email}</div>
                                        </div>
                                        <div onClick={() => openEditUser(user)} style={{ padding: '0.8rem 1rem', display: 'flex', alignItems: 'center', gap: '0.8rem', color: '#4A5568', cursor: 'pointer' }} className="hover:bg-gray-100"><MdPerson size={18} /> My Profile</div>
                                        <div onClick={logout} style={{ padding: '0.8rem 1rem', display: 'flex', alignItems: 'center', gap: '0.8rem', color: '#E53E3E', cursor: 'pointer', borderTop: '1px solid #E2E8F0' }}><MdLogout size={18} /> Sign Out</div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </header>

                <main className="px-1 py-4 sm:p-4 w-full box-border" style={{ flex: 1 }}>
                    {activeTab === 'dashboard' && (
                        <div className="fade-in">
                            {/* Stats Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                                {/* Unpaid Penalties Card (New) */}
                                <div
                                    onClick={() => setActiveTab('penalties_report')}
                                    style={{ background: 'linear-gradient(135deg, #E53E3E 0%, #C53030 100%)', borderRadius: '12px', padding: '1.1rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: '115px', cursor: 'pointer', transition: 'transform 0.2s' }}
                                    className="hover:scale-105"
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: '600', opacity: 0.9, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><MdWarning className="text-yellow-300" /> Pending Penalties</div>
                                            <div style={{ fontSize: '1.9rem', fontWeight: '800', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>₹{stats.totalPenalties || 0}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px' }}>
                                            <MdWarning size={20} color="white" />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.4rem' }}>
                                        To collect from centers
                                    </div>
                                </div>
                                {/* Total Admissions Card */}
                                <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', padding: '1.1rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: '115px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: '600', opacity: 0.9 }}>Confirmed Admissions</div>
                                            <div style={{ fontSize: '1.9rem', fontWeight: '800', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stats.totalStudents}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px' }}>
                                            <MdPerson size={20} color="white" />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.4rem' }}>
                                        Verified Students
                                    </div>
                                </div>

                                {/* Pending Admissions Card */}
                                <div style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', borderRadius: '12px', padding: '1.1rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: '115px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: '600', opacity: 0.9 }}>Total Registrations</div>
                                            <div style={{ fontSize: '1.9rem', fontWeight: '800', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stats.pendingAdmissions}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px' }}>
                                            <MdPendingActions size={20} color="white" />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.4rem' }}>
                                        All time registrations
                                    </div>
                                </div>

                                {/* Total Revenue Card */}
                                <div style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', borderRadius: '12px', padding: '1.1rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: '115px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: '600', opacity: 0.9 }}>Total Revenue</div>
                                            <div style={{ fontSize: '1.9rem', fontWeight: '800', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>₹{(stats.totalRevenue || 0).toLocaleString()}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}>
                                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>₹</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.4rem' }}>
                                        Lifetime earnings
                                    </div>
                                </div>
                            </div>

                            {/* Dashboard Content Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>

                                {/* Recent Admissions List */}
                                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2D3748', margin: 0 }}>Recent Admissions</h3>
                                        <button onClick={() => setActiveTab('students')} style={{ color: '#319795', background: 'transparent', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                            View All <MdChevronRight />
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        {admissions.slice(0, 5).map((student, index) => (
                                            <div key={student.id || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '1rem', borderBottom: index < 4 ? '1px solid #EDF2F7' : 'none' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#4A5568' }}>
                                                        {student.student_name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: '700', color: '#2D3748', fontSize: '0.95rem' }}>{student.student_name}</div>
                                                        <div style={{ fontSize: '0.8rem', color: '#718096' }}>{student.course} • {new Date(student.created_at).toLocaleDateString()}</div>
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {student.registration_no ? (
                                                        <div style={{ fontSize: '0.85rem', color: '#4A5568', fontWeight: '500' }}>{student.registration_no}</div>
                                                    ) : (
                                                        <div style={{ fontSize: '0.8rem', color: '#E53E3E', fontWeight: '600' }}>Processing</div>
                                                    )}
                                                    <span style={{ padding: '2px 8px', background: '#C6F6D5', color: '#22543D', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '4px', display: 'inline-block' }}>Verified</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Revenue Chart (Simple CSS Bar Chart) */}
                                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2D3748', margin: 0 }}>Monthly Overview</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#718096' }}>
                                            <MdTrendingUp color="#38A169" /> Reports & Admissions
                                        </div>
                                    </div>

                                    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingLeft: '10px', height: '200px' }}>
                                        {/* Mock Data for Visualization - In real app, calculate from 'admissions' date */}
                                        {[65, 40, 75, 55, 85, stats.totalStudents > 0 ? 90 : 20].map((h, i) => (
                                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                                                <div style={{
                                                    width: '60%',
                                                    height: `${h * 1.5}px`,
                                                    background: i === 5 ? '#3182CE' : '#E2E8F0',
                                                    borderRadius: '6px 6px 0 0',
                                                    transition: 'height 1s ease-out'
                                                }}></div>
                                                <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: '600' }}>{['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'][i]}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                    {activeTab === 'students' && (
                        <div className="fade-in">
                            {/* Modern Top Header Strip */}
                            <div style={{
                                background: 'linear-gradient(90deg, #2C5282 0%, #2B6CB0 100%)',
                                padding: '0.8rem 1.5rem',
                                borderRadius: '8px 8px 0 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                marginBottom: '0'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                    <MdSchool className="text-white" size={22} style={{ color: 'white' }} />
                                    <h2 style={{ fontSize: '1rem', fontWeight: '700', color: 'white', margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                        All Students Directory
                                    </h2>
                                </div>

                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    {/* Search Box */}
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        <MdSearch style={{ position: 'absolute', left: '8px', color: 'rgba(255,255,255,0.7)' }} />
                                        <input
                                            placeholder="Search Name, Reg No..."
                                            value={searchQuery}
                                            onChange={(e) => { setSearchQuery(e.target.value); }}
                                            style={{
                                                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                                                color: 'white', padding: '0.45rem 0.8rem 0.45rem 2.2rem', borderRadius: '4px', fontSize: '0.85rem', outline: 'none',
                                                minWidth: '180px'
                                            }}
                                            className="placeholder-white"
                                        />
                                    </div>

                                    {/* Payment Status Filter */}
                                    <div style={{ position: 'relative' }}>
                                        <select
                                            value={filters.paymentStatus}
                                            onChange={(e) => { setFilters({ ...filters, paymentStatus: e.target.value }); }}
                                            style={{
                                                appearance: 'none',
                                                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e")`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'calc(100% - 10px) center',
                                                backgroundSize: '16px',
                                                background: filters.paymentStatus === 'pending' ? '#FBD38D' : 'rgba(255,255,255,0.15)',
                                                border: '1px solid rgba(255,255,255,0.3)',
                                                color: filters.paymentStatus === 'pending' ? '#744210' : 'white',
                                                padding: '0.4rem 2rem 0.4rem 0.8rem', borderRadius: '4px', fontSize: '0.85rem', outline: 'none',
                                                cursor: 'pointer', minWidth: '115px', fontWeight: 'bold'
                                            }}
                                        >
                                            <option value="paid" style={{ color: 'black' }}>✓ Verified Only</option>
                                            <option value="pending" style={{ color: 'black' }}>⚠ Pending</option>
                                            <option value="all" style={{ color: 'black' }}>All Status</option>
                                        </select>
                                    </div>

                                    {/* Conducted By Filter */}
                                    <div style={{ position: 'relative' }}>
                                        <select
                                            value={filters.conductedBy}
                                            onChange={(e) => { setFilters({ ...filters, conductedBy: e.target.value }); }}
                                            style={{
                                                appearance: 'none',
                                                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e")`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'calc(100% - 10px) center',
                                                backgroundSize: '16px',
                                                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                                                color: 'white', padding: '0.4rem 2rem 0.4rem 0.8rem', borderRadius: '4px', fontSize: '0.85rem', outline: 'none',
                                                cursor: 'pointer', minWidth: '125px'
                                            }}
                                        >
                                            <option value="" style={{ color: 'black' }}>By Conductor</option>
                                            {allConductors.map(c => (
                                                <option key={c.id} value={c.id} style={{ color: 'black' }}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>


                                    {/* Combined Month-Year Filter */}
                                    <div>
                                        <select
                                            value={filters.filterMonthYear || ''}
                                            onChange={(e) => { setFilters({ ...filters, filterMonthYear: e.target.value }); }}
                                            style={{
                                                appearance: 'none',
                                                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e")`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'calc(100% - 10px) center',
                                                backgroundSize: '16px',
                                                background: 'rgba(255,255,255,0.15)',
                                                border: '1px solid rgba(255,255,255,0.3)',
                                                color: 'white',
                                                padding: '0.4rem 2rem 0.4rem 0.7rem',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                outline: 'none',
                                                cursor: 'pointer',
                                                minWidth: '135px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            <option value="" style={{ color: 'black' }}>All Months</option>
                                            {(() => {
                                                const MN = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                                const opts = [];
                                                const now = new Date();
                                                for (let i = 0; i < 18; i++) {
                                                    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                                                    const m = d.getMonth() + 1;
                                                    const y = d.getFullYear();
                                                    opts.push(<option key={`${m}-${y}`} value={`${m}-${y}`} style={{ color: 'black' }}>{MN[m]} {y}</option>);
                                                }
                                                return opts;
                                            })()}
                                        </select>
                                    </div>

                                    {/* Export Button */}
                                    <button
                                        onClick={handleExport}
                                        disabled={isExporting}
                                        style={{
                                            background: 'white', color: '#276749', border: 'none', borderRadius: '4px',
                                            padding: '0.45rem 0.8rem', fontWeight: '700', cursor: isExporting ? 'wait' : 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem',
                                            opacity: isExporting ? 0.7 : 1
                                        }}
                                    >
                                        <MdFileDownload size={18} /> {isExporting ? 'Exporting...' : 'Export'}
                                    </button>
                                </div>
                            </div>

                            <div style={{ background: 'white', borderRadius: '0 0 8px 8px', border: '1px solid #E2E8F0', borderTop: 'none', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table students={paginatedStudents} loading={loading} onViewDetails={(s) => setSelectedStudent(s)} onStatusChange={fetchAdmissions} />
                                </div>
                                <div style={{ padding: '1rem', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FAFAFA' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#718096' }}>Showing {serverTotal > 0 ? (serverPage - 1) * itemsPerPage + 1 : 0} to {Math.min(serverPage * itemsPerPage, serverTotal)} of {serverTotal} entries</span>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button disabled={serverPage === 1} onClick={() => handlePageChange(serverPage - 1)} style={{ padding: '0.4rem 0.8rem', border: '1px solid #E2E8F0', background: serverPage === 1 ? '#EDF2F7' : 'white', cursor: serverPage === 1 ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center' }}><MdChevronLeft /> Previous</button>
                                        <button disabled={serverPage === totalPages || totalPages === 0} onClick={() => handlePageChange(serverPage + 1)} style={{ padding: '0.4rem 0.8rem', border: '1px solid #E2E8F0', background: serverPage === totalPages ? '#EDF2F7' : 'white', cursor: serverPage === totalPages ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>Next <MdChevronRight /></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'free_admissions' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>Loading Free Admissions...</div>}>
                                <AdminFreeAdmissions />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'half_admissions' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>Loading Half Admissions...</div>}>
                                <AdminHalfAdmissions />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'performance' && (
                        <div className="fade-in">
                            {/* Header with Filters */}
                            <div style={{
                                background: 'linear-gradient(90deg, #6B46C1 0%, #553C9A 100%)',
                                padding: '0.8rem 1.5rem',
                                borderRadius: '8px 8px 0 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '1rem',
                                flexWrap: 'wrap',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                    <MdGroupWork size={22} color="white" />
                                    <h2 style={{ fontSize: '1rem', fontWeight: '700', color: 'white', margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                        Admissions Performance
                                    </h2>
                                    <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '800' }}>
                                        {filteredPerfStats.length} Centers
                                    </span>
                                </div>

                                {/* Filters */}
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>

                                    {/* Center Filter */}
                                    <div style={{ display: 'flex', alignItems: 'center', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.35)' }}>
                                        <div style={{ background: 'rgba(255,255,255,0.25)', padding: '0 0.45rem', height: '100%', display: 'flex', alignItems: 'center' }}>
                                            <MdLocationCity size={15} color="white" />
                                        </div>
                                        <select
                                            value={perfFilterCenter}
                                            onChange={e => setPerfFilterCenter(e.target.value)}
                                            style={{
                                                background: 'rgba(255,255,255,0.15)', border: 'none',
                                                color: 'white', padding: '0.32rem 0.5rem', borderRadius: '0',
                                                fontSize: '0.74rem', fontWeight: '600', outline: 'none', cursor: 'pointer', minWidth: '150px'
                                            }}
                                        >
                                            <option value="" style={{ color: '#2D3748', background: 'white' }}>All Centers</option>
                                            {perfCentersList.map(c => (
                                                <option key={c} value={c} style={{ color: '#2D3748', background: 'white' }}>{c}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Month Filter */}
                                    <div style={{ display: 'flex', alignItems: 'center', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.35)' }}>
                                        <div style={{ background: 'rgba(255,255,255,0.25)', padding: '0 0.45rem', height: '100%', display: 'flex', alignItems: 'center' }}>
                                            <MdCalendarMonth size={15} color="white" />
                                        </div>
                                        <select
                                            value={perfFilterMonth}
                                            onChange={e => setPerfFilterMonth(e.target.value)}
                                            style={{
                                                background: 'rgba(255,255,255,0.15)', border: 'none',
                                                color: 'white', padding: '0.32rem 0.5rem', borderRadius: '0',
                                                fontSize: '0.74rem', fontWeight: '600', outline: 'none', cursor: 'pointer', minWidth: '148px'
                                            }}
                                        >
                                            <option value="" style={{ color: '#2D3748', background: 'white' }}>All Months</option>
                                            {perfMonthOptions.map(opt => (
                                                <option key={opt.value} value={opt.value} style={{ color: '#2D3748', background: 'white' }}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Clear Filters */}
                                    {(perfFilterCenter || perfFilterMonth) && (
                                        <button
                                            onClick={() => { setPerfFilterCenter(''); setPerfFilterMonth(''); }}
                                            style={{
                                                padding: '0.32rem 0.7rem', background: 'white', border: 'none',
                                                borderRadius: '4px', color: '#6B46C1', fontWeight: '800',
                                                fontSize: '0.72rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem'
                                            }}
                                        >
                                            <MdClose size={13} /> Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Summary Stats Bar */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', background: 'white', border: '1px solid #E2E8F0', borderTop: 'none' }}>
                                <div style={{ padding: '0.65rem 1rem', background: '#FAF5FF', display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.62rem', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Total Admissions</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#553C9A' }}>
                                        {filteredPerfStats.reduce((s, r) => s + r.count, 0)}
                                    </span>
                                </div>
                                <div style={{ padding: '0.65rem 1rem', background: '#F0FFF4', borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.62rem', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Total Earnings</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#276749' }}>
                                        ₹{filteredPerfStats.reduce((s, r) => s + (perfFilterMonth ? r.monthly_earning : r.monthly_earning), 0).toLocaleString()}
                                    </span>
                                </div>
                                <div style={{ padding: '0.65rem 1rem', background: '#EBF8FF', borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.62rem', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Active Centers</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#2B6CB0' }}>
                                        {[...new Set(filteredPerfStats.map(r => r.center_name))].length}
                                    </span>
                                </div>
                                <div style={{ padding: '0.65rem 1rem', background: '#FFFAF0', borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.62rem', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>
                                        {perfFilterMonth ? 'Month Filter Active' : 'Today Admissions'}
                                    </span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#D69E2E' }}>
                                        {perfFilterMonth
                                            ? perfMonthOptions.find(o => o.value === perfFilterMonth)?.label
                                            : filteredPerfStats.reduce((s, r) => s + r.today_count, 0)
                                        }
                                    </span>
                                </div>
                            </div>

                            {/* Table */}
                            <div style={{ background: 'white', borderRadius: '0 0 8px 8px', border: '1px solid #E2E8F0', borderTop: 'none', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                                        <thead style={{ background: '#F7FAFC' }}>
                                            <tr>
                                                <Th>#</Th>
                                                <Th>Center</Th>
                                                <Th>Conducted By</Th>
                                                <Th>Recent Student</Th>
                                                <Th>{perfFilterMonth ? 'Month Admissions' : 'Total Admissions'}</Th>
                                                <Th>{perfFilterMonth ? 'Month Earnings' : 'Earnings (Today / Month)'}</Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPerfStats.length === 0 ? (
                                                <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#A0AEC0' }}>No data for selected filter.</td></tr>
                                            ) : (
                                                filteredPerfStats.map((stat, idx) => (
                                                    <tr key={stat.id} style={{ borderBottom: '1px solid #EDF2F7', background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                                                        <Td><span style={{ color: '#A0AEC0', fontWeight: '700', fontSize: '0.8rem' }}>{idx + 1}</span></Td>
                                                        <Td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                                    {stat.center_logo_url ? (
                                                                        <img src={stat.center_logo_url} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="logo" />
                                                                    ) : (
                                                                        <MdAssignment size={18} color="#A0AEC0" />
                                                                    )}
                                                                </div>
                                                                <span style={{ fontWeight: '700', color: '#2D3748' }}>{stat.center_name}</span>
                                                            </div>
                                                        </Td>
                                                        <Td><span style={{ fontWeight: '600', color: '#553C9A' }}>{stat.conducted_by}</span></Td>
                                                        <Td><span style={{ fontSize: '0.85rem', color: '#2D3748', fontWeight: '600' }}>{stat.recent_student || '-'}</span></Td>
                                                        <Td>
                                                            <span style={{ background: '#E9D8FD', color: '#553C9A', padding: '2px 10px', borderRadius: '12px', fontWeight: '800', fontSize: '0.85rem' }}>
                                                                {stat.count}
                                                            </span>
                                                        </Td>
                                                        <Td>
                                                            {perfFilterMonth ? (
                                                                <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#276749' }}>₹{stat.monthly_earning}</span>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                    <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#276749' }}>Today: ₹{stat.today_earning}</span>
                                                                    <span style={{ fontSize: '0.75rem', color: '#718096' }}>Monthly: ₹{stat.monthly_earning}</span>
                                                                </div>
                                                            )}
                                                        </Td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="fade-in">
                            {/* Modern Top Header Strip */}
                            <div style={{
                                background: 'linear-gradient(90deg, #2C5282 0%, #2B6CB0 100%)',
                                padding: '0.8rem 1.5rem',
                                borderRadius: '8px 8px 0 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                marginBottom: '0'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                    <MdSupervisedUserCircle className="text-white" size={22} style={{ color: 'white' }} />
                                    <h2 style={{ fontSize: '1rem', fontWeight: '700', color: 'white', margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                        User Management
                                    </h2>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <div style={{ position: 'relative' }}>
                                        <MdSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)' }} size={18} />
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            value={userSearchQuery}
                                            onChange={(e) => setUserSearchQuery(e.target.value)}
                                            style={{
                                                background: 'rgba(255,255,255,0.15)',
                                                border: '1px solid rgba(255,255,255,0.3)',
                                                color: 'white',
                                                padding: '0.45rem 1rem 0.45rem 2.2rem',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                width: '200px',
                                                outline: 'none'
                                            }}
                                            className="placeholder-white"
                                        />
                                    </div>
                                    <button
                                        onClick={openAddUser}
                                        style={{
                                            background: 'white', color: '#2B6CB0', padding: '0.45rem 1rem', borderRadius: '4px',
                                            border: 'none', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                            gap: '0.5rem', fontSize: '0.85rem'
                                        }}
                                    >
                                        <MdAdd size={18} /> Add New User
                                    </button>
                                </div>
                            </div>

                            <div style={{ background: 'white', borderRadius: '0 0 8px 8px', border: '1px solid #E2E8F0', borderTop: 'none', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ background: '#F7FAFC', borderBottom: '2px solid #E2E8F0' }}>
                                        <tr><Th>User / Email</Th><Th>Designation</Th><Th>Center Name</Th><Th>Commission</Th><Th>Role</Th><Th>Action</Th></tr>
                                    </thead>
                                    <tbody>
                                        {users.filter(u =>
                                            u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                            u.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                            u.center_name?.toLowerCase().includes(userSearchQuery.toLowerCase())
                                        ).map(u => (
                                            <tr key={u.id} style={{ borderBottom: '1px solid #F7FAFC' }}>
                                                <Td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#E2E8F0', overflow: 'hidden' }}>{(u.profile_photo_url || u.center_logo_url) ? <img src={u.profile_photo_url || u.center_logo_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="P" /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#718096' }}>{u.name.charAt(0)}</div>}</div>
                                                        <div><div style={{ fontWeight: '700', color: '#2D3748' }}>{u.name}</div><div style={{ fontSize: '0.8rem', color: '#718096' }}>{u.email}</div></div>
                                                    </div>
                                                </Td>
                                                <Td>{u.designation || '-'}</Td>
                                                <Td>{u.center_name || 'N/A'}</Td>
                                                <Td>
                                                    {['faculty', 'lf', 'cc'].includes(u.role) ? (
                                                        <span style={{ fontWeight: '700', color: '#2C7A7B' }}>₹{parseFloat(u.commission_per_admission || 0)}</span>
                                                    ) : '-'}
                                                </Td>
                                                <Td>
                                                    <span style={{
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        background: u.role === 'admin' ? '#FED7D7' : (u.role === 'hardware_engineer' ? '#BEE3F8' : (u.role === 'auto_driver' ? '#E9D8FD' : '#C6F6D5')),
                                                        color: u.role === 'admin' ? '#822727' : (u.role === 'hardware_engineer' ? '#2B6CB0' : (u.role === 'auto_driver' ? '#553C9A' : '#22543D'))
                                                    }}>
                                                        {u.role.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                </Td>
                                                <Td>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button onClick={() => openEditUser(u)} style={{ color: '#3182CE', background: 'transparent', border: 'none', cursor: 'pointer' }}><MdEdit size={20} /></button>
                                                        <button onClick={() => handleDeleteUser(u)} style={{ color: '#E53E3E', background: 'transparent', border: 'none', cursor: 'pointer' }}><MdDelete size={20} /></button>
                                                    </div>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}


                    {activeTab === 'inspections' && <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><AdminInspectionGallery /></React.Suspense>}
                    {activeTab === 'hardware' && <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><AdminHardwareReports /></React.Suspense>}
                    {activeTab === 'inventory' && <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><InventoryManager users={users} /></React.Suspense>}
                    {activeTab === 'penalties_report' && <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><AdminPenaltyReport onBack={() => setActiveTab('dashboard')} /></React.Suspense>}
                    {activeTab === 'target' && <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><AdminTargetManagement /></React.Suspense>}

                    {activeTab === 'notices' && (
                        <div className="fade-in h-[calc(100vh-140px)]">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <AdminNoticeManager />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'kyp_apps' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <KushalYuvaAdminView />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'autos' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <AdminAutoManagement />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'teachers' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <AdminTeacherManager />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'drcc_approvals' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <AdminDrccApprovals />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'facilities' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <AdminCenterFacilities users={users} />
                            </React.Suspense>
                        </div>
                    )}

                    {activeTab === 'govt_tracker' && (
                        <div className="fade-in">
                            <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
                                <AdminGovtTracker />
                            </React.Suspense>
                        </div>
                    )}




                </main >
            </div >

            <AnimatePresence>
                {isUserModalOpen && <UserModal user={editingUser} currentUser={user} onClose={() => setIsUserModalOpen(false)} onSuccess={handleUserModalSuccess} />}
            </AnimatePresence>

            <AnimatePresence>
                {selectedStudent && <StudentProfileModal student={selectedStudent} onClose={() => setSelectedStudent(null)} onSuccess={fetchAdmissions} />}
            </AnimatePresence>



            <style>{`
                @media (max-width: 900px) {
                    .mobile-overlay { display: block !important; }
                    .main-content { margin-left: 0 !important; }
                    .mobile-only-close { display: block !important; }
                }
                .placeholder-white::placeholder {
                    color: rgba(255, 255, 255, 0.8) !important;
                }
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div >
    );
}

// Components
const NavItem = React.memo(({ icon, label, isActive, onClick, badge, isCollapsed }) => {
    return (
        <div onClick={onClick} style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: isCollapsed ? '0' : '1rem',
            padding: isCollapsed ? '0.72rem 0' : '0.72rem 1.5rem', // Center and remove side padding if collapsed
            margin: '0.5px 0',
            background: isActive ? 'linear-gradient(90deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0) 100%)' : 'transparent',
            color: isActive ? '#10b981' : '#94a3b8',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            fontWeight: '700',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            whiteSpace: 'nowrap',
            borderRight: isActive ? '4px solid #10b981' : '4px solid transparent',
            boxShadow: isActive ? 'inset -2px 0 10px rgba(16, 185, 129, 0.05)' : 'none',
            width: '100%',
            boxSizing: 'border-box'
        }} className={!isActive ? "hover:bg-white/5 hover:text-gray-200" : ""} title={isCollapsed ? label : ""}>
            <div style={{ fontSize: '1.25rem', display: 'flex', opacity: isActive ? 1 : 0.7, justifyContent: 'center', alignItems: 'center' }}>{icon}</div>

            {!isCollapsed && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', flex: 1, position: 'relative' }}>
                    <span style={{ flex: 1 }}>{label}</span>
                    {badge && (
                        <div style={{
                            position: 'absolute',
                            top: '-18px', // Positioned in the extra padding space
                            right: '0',
                            background: '#ef4444',
                            color: 'white',
                            fontSize: '9px',
                            padding: '1px 8px',
                            borderRadius: '2px 12px 2px 12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '900',
                            boxShadow: '0 0 10px rgba(239, 68, 68, 0.8)', // Stronger 'Highlight' glow
                            transform: 'rotate(-7deg)',
                            zIndex: 1,
                            whiteSpace: 'nowrap'
                        }}>
                            {badge}
                            {/* More pronounced slanted indicator on the LEFT */}
                            <div style={{
                                position: 'absolute',
                                bottom: '-5px',
                                left: '4px',
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderTop: '5px solid #ef4444',
                                transform: 'rotate(-25deg)'
                            }} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
});


function StatCard({ label, value, icon, bg }) {
    return (
        <div style={{ background: bg, padding: '1.5rem', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '110px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', opacity: 0.9 }}>{label}</span>
                <div style={{ fontSize: '1.4rem', opacity: 0.8 }}>{icon}</div>
            </div>
            <h3 style={{ fontSize: '1.8rem', fontWeight: '800', margin: 0 }}>{value}</h3>
        </div>
    )
}

const Th = React.memo(({ children }) => <th style={{ verticalAlign: 'middle', padding: '0.4rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</th>);
const Td = React.memo(({ children }) => <td style={{ verticalAlign: 'middle', padding: '0.4rem 1rem', fontSize: '0.9rem', color: '#4A5568' }}>{children}</td>);

function Table({ students, loading, onViewDetails, onStatusChange }) {
    const [localStatuses, setLocalStatuses] = React.useState({});
    const [confirmModal, setConfirmModal] = React.useState({ show: false, message: '', onYes: null, pendingSelectRef: null, oldVal: null });

    React.useEffect(() => {
        const map = {};
        students.forEach(s => { map[s.id] = s.admin_status || 'Pending'; });
        setLocalStatuses(map);
    }, [students]);

    const showConfirm = (message, onYes, selectRef, oldVal) => {
        setConfirmModal({ show: true, message, onYes, pendingSelectRef: selectRef, oldVal });
    };

    const handleConfirmYes = () => {
        if (confirmModal.onYes) confirmModal.onYes();
        setConfirmModal({ show: false, message: '', onYes: null, pendingSelectRef: null, oldVal: null });
    };

    const handleConfirmNo = () => {
        if (confirmModal.pendingSelectRef) confirmModal.pendingSelectRef.value = confirmModal.oldVal;
        setConfirmModal({ show: false, message: '', onYes: null, pendingSelectRef: null, oldVal: null });
    };

    if (loading) {
        return (
            <div style={{ padding: '1rem' }}>
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="skeleton" style={{ height: '60px', borderRadius: '8px', marginBottom: '10px', background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'skeleton-loading 1.5s infinite linear' }}></div>
                ))}
            </div>
        );
    }
    if (students.length === 0) return <div style={{ padding: '2rem', textAlign: 'center', color: '#718096' }}>No admissions found.</div>;

    const handleAdminStatusChange = (studentId, newStatus, selectRef) => {
        const oldStatus = localStatuses[studentId] || 'Pending';

        if (newStatus === 'Confirm') {
            showConfirm(
                'Kya yeh admission Bihar Govt ke portal par ho gaya hai?',
                async () => {
                    try {
                        await api.put(`/admissions/${studentId}/admin-status`, { admin_status: newStatus });
                        setLocalStatuses(prev => ({ ...prev, [studentId]: newStatus }));
                        if (onStatusChange) onStatusChange();
                    } catch { alert('Status update failed.'); if (selectRef) selectRef.value = oldStatus; }
                },
                selectRef, oldStatus
            );
        } else {
            showConfirm(
                'Kya yeh admission abhi tak Bihar Govt ke portal par nahi hua hai? Isse wapis Pending kiya jaayega.',
                async () => {
                    try {
                        await api.put(`/admissions/${studentId}/admin-status`, { admin_status: newStatus });
                        setLocalStatuses(prev => ({ ...prev, [studentId]: newStatus }));
                        if (onStatusChange) onStatusChange();
                    } catch { alert('Status update failed.'); if (selectRef) selectRef.value = oldStatus; }
                },
                selectRef, oldStatus
            );
        }
    };

    return (
        <>
            {/* Custom Confirm Popup */}
            {confirmModal.show && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.45)', padding: '1rem'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '8px',
                        padding: '1.4rem 1.2rem', width: '100%', maxWidth: '320px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.25)'
                    }}>
                        <div style={{ fontSize: '1.3rem', marginBottom: '0.6rem' }}>❓</div>
                        <p style={{ margin: '0 0 1.2rem', fontWeight: '600', color: '#1A202C', fontSize: '0.92rem', lineHeight: 1.5 }}>
                            {confirmModal.message}
                        </p>
                        <div style={{ display: 'flex', gap: '0.6rem' }}>
                            <button onClick={handleConfirmYes} style={{
                                flex: 1, padding: '0.55rem', background: '#38A169',
                                color: 'white', border: 'none', borderRadius: '4px',
                                fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer'
                            }}>✓ Haan</button>
                            <button onClick={handleConfirmNo} style={{
                                flex: 1, padding: '0.55rem', background: '#EDF2F7',
                                color: '#4A5568', border: 'none', borderRadius: '4px',
                                fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer'
                            }}>✗ Nahi</button>
                        </div>
                    </div>
                </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '750px' }}>
                <thead style={{ background: '#EDF2F7', borderBottom: '2px solid #E2E8F0' }}>
                    <tr><Th>Photo</Th><Th>Student Name</Th><Th>Reg No</Th><Th>Course</Th><Th>Batch</Th><Th>Amount</Th><Th>Date</Th><Th>Center</Th><Th>Gov Portal</Th></tr>
                </thead>
                <tbody>
                    {students.map((student) => (
                        <TableRow
                            key={student.id}
                            student={student}
                            currentStatus={localStatuses[student.id] || student.admin_status || 'Pending'}
                            onViewDetails={onViewDetails}
                            handleAdminStatusChange={handleAdminStatusChange}
                        />
                    ))}
                </tbody>
            </table>
        </>
    );
}

const TableRow = React.memo(({ student, currentStatus, onViewDetails, handleAdminStatusChange }) => {
    const isConfirmed = currentStatus === 'Confirm';
    return (
        <tr style={{
            borderBottom: '1px solid #E2E8F0',
            transition: 'background 0.2s',
            background: isConfirmed ? '#F0FFF4' : 'white'
        }}
            onMouseEnter={(e) => e.currentTarget.style.background = isConfirmed ? '#C6F6D5' : '#E6FFFA'}
            onMouseLeave={(e) => e.currentTarget.style.background = isConfirmed ? '#F0FFF4' : 'white'}
        >
            <Td>
                <div onClick={() => onViewDetails(student)} style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#E2E8F0', overflow: 'hidden', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', cursor: 'pointer' }}>
                    {student.photo_url ? <img src={student.photo_url} alt="S" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#A0AEC0', fontSize: '0.8rem' }}>{student.student_name.charAt(0)}</div>}
                </div>
            </Td>
            <Td><span onClick={() => onViewDetails(student)} style={{ cursor: 'pointer', fontWeight: isConfirmed ? '700' : 'normal', color: isConfirmed ? '#276749' : '#2D3748' }}>{student.student_name}</span></Td>
            <Td><span onClick={() => onViewDetails(student)} style={{ cursor: 'pointer' }}>{student.registration_no || <span style={{ color: '#E53E3E', fontSize: '0.8rem', fontWeight: 'bold' }}>Unassigned</span>}</span></Td>
            <Td><span style={{ padding: '4px 10px', background: '#EDF2F7', borderRadius: '4px', fontSize: '0.8rem', color: '#4A5568', fontWeight: '600', cursor: 'pointer' }} onClick={() => onViewDetails(student)}>{student.course}</span></Td>
            <Td><span onClick={() => onViewDetails(student)} style={{ cursor: 'pointer' }}>{student.batch}</span></Td>
            <Td style={{ fontWeight: '600', color: '#2D3748', cursor: 'pointer' }} onClick={() => onViewDetails(student)}>₹{student.payment_amount}</Td>
            <Td><span onClick={() => onViewDetails(student)} style={{ cursor: 'pointer' }}>{student.admission_date}</span></Td>
            <Td><span onClick={() => onViewDetails(student)} style={{ fontSize: '0.85rem', fontWeight: '700', color: '#319795', cursor: 'pointer' }}>{student.center_name}</span></Td>
            <Td>
                <select
                    value={currentStatus}
                    onChange={(e) => { const sel = e.target; handleAdminStatusChange(student.id, e.target.value, sel); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        padding: '4px 8px', borderRadius: '4px',
                        border: isConfirmed ? '1px solid #9AE6B4' : '1px solid #CBD5E0',
                        background: isConfirmed ? '#C6F6D5' : 'white',
                        fontSize: '0.8rem', fontWeight: 'bold',
                        color: isConfirmed ? '#276749' : '#4A5568',
                        outline: 'none', cursor: 'pointer'
                    }}
                >
                    <option value="Pending">Pending</option>
                    <option value="Confirm">Confirm</option>
                </select>
            </Td>
        </tr>
    )
});
function UserModal({ user, currentUser, onClose, onSuccess }) {
    const isEdit = !!user;
    const isSelf = user?.id === currentUser?.id;
    const [formData, setFormData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        password: '',
        role: user?.role || 'faculty',
        designation: user?.designation || '',
        commission_per_admission: user?.commission_per_admission ?? 0,
        center_name: user?.center_name || '',
        center_address: user?.center_address || '',
        permissions: user?.permissions || []
    });
    const [logo, setLogo] = useState(null);
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const data = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'permissions') {
                data.append(key, JSON.stringify(formData[key]));
            } else {
                data.append(key, formData[key]);
            }
        });
        if (logo) data.append('center_logo', logo);
        if (profilePhoto) data.append('profile_photo', profilePhoto);
        if (isEdit) data.append('_method', 'PUT');

        try {
            if (isEdit) await api.post(`/users/${user.id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
            else await api.post('/users', data, { headers: { 'Content-Type': 'multipart/form-data' } });
            onSuccess(isSelf);
            onClose();
        } catch (err) {
            console.error('User creation failed:', err);
            const msg = err.response?.data?.message || 'Failed to save user.';
            alert(`Error: ${msg}`);
        } finally {
            setLoading(false);
        }
    }

    const inputStyle = { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #E2E8F0' };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }}>
            <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                style={{ 
                    background: 'white', 
                    borderRadius: '12px', 
                    width: '90%', 
                    maxWidth: '520px', 
                    maxHeight: '85vh', 
                    display: 'flex', 
                    flexDirection: 'column',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden'
                }}
            >
                {/* Fixed Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2rem 1.5rem', borderBottom: '1px solid #E2E8F0' }}>
                    <h3 style={{ margin: 0, fontWeight: '700', fontSize: '1.2rem', color: '#1A202C' }}>{isSelf ? 'My Profile' : (isEdit ? 'Edit User' : 'Add New User')}</h3>
                    <MdClose onClick={onClose} style={{ cursor: 'pointer', color: '#4A5568' }} size={24} />
                </div>

                {/* Form Wrapper */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', margin: 0 }}>
                    {/* Scrollable Content Area */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'grid', gap: '1rem' }}>

                        {/* Common Fields */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <input required placeholder="Authorised Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
                            <input required type="text" placeholder="Email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} style={{ ...inputStyle, background: isEdit ? '#F7FAFC' : 'white', cursor: isEdit ? 'not-allowed' : 'text' }} disabled={isEdit} autoComplete="email" />
                        </div>

                        {/* Phone and Username both removed as per request */}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <input type="password" placeholder={isEdit ? "New Password" : "Password"} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} style={inputStyle} required={!isEdit} autoComplete="new-password" />
                            <input placeholder="Designation" value={formData.designation} onChange={e => setFormData({ ...formData, designation: e.target.value })} style={inputStyle} required />
                        </div>

                        <div>
                            <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#4A5568', display: 'block', marginBottom: '0.5rem' }}>Select Role <span style={{ color: 'red' }}>*</span></label>
                            <select
                                value={formData.role}
                                onChange={e => setFormData({ ...formData, role: e.target.value, permissions: [] })}
                                style={inputStyle}
                            >
                                <option value="faculty">Faculty (Center User)</option>
                                <option value="lf">Learning Facilitator (LF)</option>
                                <option value="cc">Center Coordinator (CC)</option>
                                <option value="admin">Admin (System User)</option>
                                <option value="sub_admin">Sub Admin (Limited Admin)</option>
                                <option value="hardware_engineer">Hardware Engineer (Maintenance)</option>
                                {isEdit && formData.role === 'auto_driver' && (
                                    <option value="auto_driver">Auto Driver (Fleet)</option>
                                )}
                            </select>
                        </div>

                        {/* SUB ADMIN Permissions Section */}
                        {formData.role === 'sub_admin' && (
                            <div style={{ background: '#FFF5F7', padding: '1rem', borderRadius: '8px', border: '1px solid #FEB2C8' }}>
                                <h4 style={{ margin: '0 0 0.8rem 0', fontSize: '0.9rem', color: '#702459', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    🔐 Sub Admin — Admin Module Access
                                </h4>
                                <p style={{ fontSize: '0.78rem', color: '#97266D', margin: '0 0 0.8rem 0' }}>
                                    Sirf checked modules hi Sub Admin ke Admin panel mein dikhenge.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                                    {[
                                        { id: 'admissions', label: '📋 Admissions Management' },
                                        { id: 'users', label: '👥 User Management' },
                                        { id: 'performance', label: '📊 Performance / Reports' },
                                        { id: 'centers', label: '🏫 Centers Overview' },
                                        { id: 'leads', label: '📞 Leads Management' },
                                        { id: 'notices', label: '📢 Notice Board' },
                                        { id: 'inspections', label: '🔍 Center Inspections' },
                                        { id: 'inventory', label: '📦 Inventory Mgmt' },
                                        { id: 'hardware', label: '🖥️ Hardware Mgmt' },
                                        { id: 'penalties', label: '⚠️ Penalties' },
                                        { id: 'monitoring', label: '📡 Monitoring' },
                                        { id: 'kyp', label: '📝 KYP Registrations' },
                                        { id: 'performance', label: '📊 Admission Target' },
                                    ].map(p => (
                                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', cursor: 'pointer', color: '#553C9A' }}>
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(formData.permissions) && formData.permissions.includes(p.id)}
                                                onChange={e => {
                                                    const perms = Array.isArray(formData.permissions) ? formData.permissions : [];
                                                    const newPerms = e.target.checked
                                                        ? [...perms, p.id]
                                                        : perms.filter(x => x !== p.id);
                                                    setFormData({ ...formData, permissions: newPerms });
                                                }}
                                            />
                                            {p.label}
                                        </label>
                                    ))}
                                </div>
                                <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
                                    <button type="button" onClick={() => setFormData({ ...formData, permissions: ['admissions', 'users', 'performance', 'centers', 'leads', 'notices', 'inspections', 'inventory', 'hardware', 'penalties', 'monitoring', 'kyp'] })}
                                        style={{ fontSize: '0.75rem', padding: '4px 10px', background: '#9F7AEA', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                        Select All
                                    </button>
                                    <button type="button" onClick={() => setFormData({ ...formData, permissions: [] })}
                                        style={{ fontSize: '0.75rem', padding: '4px 10px', background: '#EDF2F7', color: '#4A5568', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Permissions Section for faculty/lf/cc roles */}
                        {['faculty', 'lf', 'cc'].includes(formData.role) && (
                            <div style={{ background: '#F0F5FF', padding: '1rem', borderRadius: '8px', border: '1px solid #C3DAFE' }}>
                                <h4 style={{ margin: '0 0 0.8rem 0', fontSize: '0.9rem', color: '#2C5282', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <MdVerifiedUser /> Set Accessible Menus (Permissions)
                                </h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                                    {[
                                        { id: 'dashboard', label: 'Dashboard Overview' },
                                        { id: 'admission', label: 'New Admission' },
                                        { id: 'free_admission', label: 'Free Admission' },
                                        { id: 'half_admission', label: 'Half Admission' },
                                        { id: 'my_admissions', label: 'My Admissions' },
                                        { id: 'kyp_form', label: 'Registration Form' },
                                        { id: 'my_registrations', label: 'My Registrations' },
                                        { id: 'inspection', label: 'Daily Inspection' },
                                        { id: 'auto_approvals', label: 'Auto Management' },
                                        { id: 'hardware', label: 'Hardware Status' },
                                        { id: 'inventory', label: 'My Assets' },
                                        { id: 'center_facilities', label: 'Available Assets' },
                                        { id: 'drcc_approval', label: 'DRCC Approval (Center)' },
                                        { id: 'notices', label: 'Notice Board' }
                                    ].map(p => (
                                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', cursor: 'pointer', color: '#4A5568' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.permissions.includes(p.id)}
                                                onChange={e => {
                                                    const newPerms = e.target.checked
                                                        ? [...formData.permissions, p.id]
                                                        : formData.permissions.filter(x => x !== p.id);
                                                    setFormData({ ...formData, permissions: newPerms });
                                                }}
                                            />
                                            {p.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Conditional Fields: Admin/Engineer */}
                        {(formData.role === 'admin' || formData.role === 'hardware_engineer') && (
                            <div style={{ background: '#F7FAFC', padding: '1rem', borderRadius: '8px', border: '1px dashed #CBD5E0' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#2D3748', display: 'block', marginBottom: '0.5rem' }}>Profile Photo</label>
                                <input type="file" accept="image/*" onChange={e => setProfilePhoto(e.target.files[0])} style={{ fontSize: '0.9rem' }} />
                            </div>
                        )}

                        {/* Conditional Fields: Faculty / CC / LF / Auto Driver */}
                        {['faculty', 'lf', 'cc', 'auto_driver'].includes(formData.role) && (
                            <div style={{ background: '#F0FFF4', padding: '1rem', borderRadius: '8px', border: '1px solid #C6F6D5', display: 'grid', gap: '1rem' }}>
                                <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#276749' }}>Center & Commission</h4>

                                {['faculty', 'lf', 'cc'].includes(formData.role) && (
                                    <div>
                                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#276749', display: 'block', marginBottom: '0.4rem' }}>Commission Per Admission (₹)</label>
                                        <input
                                            type="number"
                                            placeholder="e.g. 200"
                                            value={formData.commission_per_admission}
                                            onChange={e => setFormData({ ...formData, commission_per_admission: e.target.value })}
                                            style={inputStyle}
                                            required
                                        />
                                    </div>
                                )}

                                <input placeholder="Center Name" value={formData.center_name} onChange={e => setFormData({ ...formData, center_name: e.target.value })} style={inputStyle} required />
                                <input placeholder="Center Address" value={formData.center_address} onChange={e => setFormData({ ...formData, center_address: e.target.value })} style={inputStyle} required />
                                <div>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#276749', display: 'block', marginBottom: '0.5rem' }}>Center Logo</label>
                                    <input type="file" accept="image/*" onChange={e => setLogo(e.target.files[0])} style={{ fontSize: '0.9rem' }} />
                                    {formData.center_logo_path && !logo && <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '4px' }}>Current: {formData.center_logo_path}</div>}
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Fixed Footer */}
                    <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', justifyContent: 'flex-end', borderRadius: '0 0 12px 12px' }}>
                        <button type="submit" disabled={loading} style={{ width: '100%', padding: '0.8rem', background: '#319795', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95rem' }}>
                            {loading ? 'Saving...' : 'Save User'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

function StudentProfileModal({ student, onClose, onSuccess }) {
    if (!student) return null;

    // Helper for conditional rendering
    const DetailRow = ({ icon: Icon, label, value, color }) => {
        if (!value) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.3rem 0', borderBottom: '1px solid #EDF2F7' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: color || '#E6FFFA', color: '#2C7A7B', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={18} />
                </div>
                <div>
                    <div style={{ fontSize: '0.75rem', color: '#A0AEC0', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                    <div style={{ fontSize: '0.95rem', color: '#2D3748', fontWeight: '600' }}>{value}</div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}>
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                className="no-scrollbar"
                style={{ background: 'white', borderRadius: '16px', width: '90%', maxWidth: '820px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', border: '1px solid #E2E8F0', overflow: 'hidden' }}
            >
                {/* 1. Header Banner */}
                <div style={{ height: '85px', background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)', position: 'relative' }}>
                    <button onClick={onClose} style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(255,255,255,0.2)', width: '32px', height: '32px', borderRadius: '50%', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                        <MdClose size={20} />
                    </button>
                    <div style={{ position: 'absolute', bottom: '-25px', left: '30px', display: 'flex', alignItems: 'flex-end', gap: '1.2rem' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', border: '4px solid white', background: 'white', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                            {student.photo_path ? (
                                <img src={student.photo_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="S" />
                            ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EDF2F7', color: '#A0AEC0', fontSize: '2.5rem' }}>{student.student_name?.[0]}</div>
                            )}
                        </div>
                        <div style={{ paddingBottom: '0.2rem' }}>
                            <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'white', fontWeight: '800', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{student.student_name}</h1>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <span style={{ background: '#3182CE', color: 'white', fontSize: '0.75rem', fontWeight: '700', padding: '2px 8px', borderRadius: '4px' }}>{student.course}</span>
                                <span style={{ background: 'white', color: '#2D3748', fontSize: '0.75rem', fontWeight: '700', padding: '2px 8px', borderRadius: '4px' }}>{student.batch} ({student.batch_time})</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Main Content */}
                <div style={{ paddingTop: '30px', paddingRight: '20px', paddingBottom: '20px', paddingLeft: '20px', display: 'grid', gridTemplateColumns: 'minmax(300px, 1.5fr) 1fr', gap: '1.5rem' }}>

                    {/* Left Column: Personal & Academic */}
                    <div>
                        <h3 style={{ fontSize: '0.85rem', color: '#2D3748', fontWeight: '700', borderBottom: '2px solid #E2E8F0', paddingBottom: '0.3rem', marginBottom: '0.8rem' }}>Student Information</h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <DetailRow icon={MdEmail} label="Email Address" value={student.email} />
                            <DetailRow icon={MdPhone} label="Phone Number" value={student.phone} color="#EBF8FF" />
                            <DetailRow icon={MdPerson} label="Father's Name" value={student.fathers_name} color="#F0FFF4" />
                            <DetailRow icon={MdFingerprint} label="Aadhar Number" value={student.aadhar_no} color="#FAF5FF" />
                            <DetailRow icon={MdCreditCard} label="DRCC NO" value={student.drcss_no} color="#FFF5F5" />
                            <DetailRow icon={MdLocationOn} label="Training Center" value={`${student.center_name}, ${student.center_address}`} color="#FEFCBF" />
                        </div>
                    </div>

                    {/* Right Column: Admission & System */}
                    <div>
                        {/* Admission Status Card */}
                        <div style={{ background: '#F7FAFC', borderRadius: '12px', padding: '1rem', border: '1px solid #EDF2F7', marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.6rem 0', fontSize: '0.8rem', color: '#4A5568', textTransform: 'uppercase' }}>Admission Details</h4>

                            <div style={{ display: 'grid', gap: '0.6rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#718096', fontSize: '0.85rem' }}>Registration No</span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#2D3748' }}>{student.registration_no || 'Pending'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#718096', fontSize: '0.85rem' }}>Admission Date</span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#2D3748' }}>{new Date(student.admission_date).toLocaleDateString()}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#718096', fontSize: '0.85rem' }}>Conducted By</span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#805AD5' }}>{student.conducted_by || 'System Admin'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Payment Status Card (Conditional) */}
                        {student.payment_status === 'paid' ? (
                            <div style={{ background: '#F0FFF4', borderRadius: '12px', padding: '1.5rem', border: '1px solid #C6F6D5' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                    <MdVerified size={20} color="#38A169" />
                                    <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#276749', fontWeight: '800' }}>Payment Verified</h4>
                                </div>
                                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#22543D', marginBottom: '0.5rem' }}>₹{student.payment_amount}</div>
                                <div style={{ fontSize: '0.7rem', color: '#48BB78', wordBreak: 'break-all', fontWeight: '500', marginTop: '0.5rem' }}>
                                    Order ID: {student.order_id} {student.transaction_id && ` • TXN: ${student.transaction_id}`}
                                </div>
                            </div>
                        ) : (
                            <div style={{ background: '#FFF5F5', borderRadius: '12px', padding: '1rem', border: '1px solid #FEB2B2' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem' }}>
                                    <MdWarning size={20} color="#E53E3E" />
                                    <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#C53030', fontWeight: '800' }}>Payment Pending</h4>
                                </div>

                                {student.transaction_id ? (
                                    <div style={{ marginBottom: '1rem', background: 'white', padding: '0.8rem', borderRadius: '6px', border: '1px dashed #E53E3E' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase' }}>Student Submitted UTR</div>
                                        <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#2D3748', letterSpacing: '1px' }}>{student.transaction_id}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#E53E3E', marginTop: '4px' }}>Please verify this UTR in your bank app.</div>
                                    </div>
                                ) : (
                                    <div style={{ fontSize: '0.9rem', color: '#744210', marginBottom: '1rem' }}>
                                        No UTR submitted yet. Student has not completed the manual payment step.
                                    </div>
                                )}

                                <div style={{ fontSize: '0.75rem', color: '#A0AEC0', marginBottom: '1rem' }}>
                                    Order ID: {student.order_id || 'Not Generated (Manual Flow)'}
                                </div>

                                <CheckStatusButton
                                    orderId={student.order_id}
                                    admissionId={student.id}
                                    isManual={!!student.transaction_id}
                                    onVerified={() => {
                                        if (onSuccess) onSuccess();
                                        onClose();
                                    }}
                                />
                            </div>
                        )}

                    </div>
                </div>

                {/* Footer Metadata */}
                <div style={{ padding: '0.6rem 1rem', background: '#F7FAFC', borderTop: '1px solid #E2E8F0', fontSize: '0.7rem', color: '#A0AEC0', textAlign: 'center' }}>
                    Record Created At: {new Date(student.created_at).toLocaleString()} • CMS Secure Admin Panel
                </div>

            </motion.div>
        </div>
    );
}

const CheckStatusButton = ({ orderId, admissionId, isManual, onVerified }) => {
    const [loading, setLoading] = useState(false);

    const handleCheck = async () => {
        // Fallback to constructing a manual order ID if missing
        const effectiveOrderId = orderId || (admissionId ? `MANUAL_${admissionId}` : null);

        if (!effectiveOrderId) { alert('No Order ID or Admission ID found'); return; }

        if (isManual && !window.confirm("Have you verified the UTR/Amount in your Bank Account?\n\nClick OK to Confirm Admission.")) return;

        setLoading(true);
        try {
            const response = await api.post('/admissions/verify-payment', { order_id: effectiveOrderId });
            const res = response.data;
            if (res.status === 'success') {
                alert(`Success! Admission Confirmed.\nStudent Reg No: ${res.registration_no}`);
                if (onVerified) onVerified();
            } else {
                // If Verification Fails, Offer Manual Override
                if (window.confirm(`Payment Verification Failed: ${res.message}\n\nDo you want to FORCE APPROVE this as a Manual Payment?\n(Only do this if you received money)`)) {
                    await handleForceApprove();
                }
            }
        } catch (error) {
            console.error(error);
            if (window.confirm("Error contacting Payment Gateway.\n\nDo you want to FORCE APPROVE as Manual Payment?\n(Only do this if you received money)")) {
                await handleForceApprove();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForceApprove = async () => {
        try {
            const response = await api.post('/admissions/force-approve', { admission_id: admissionId });
            if (response.data.status === 'success') {
                alert(`Manual Approval Successful!\nReg No: ${response.data.registration_no}`);
                if (onVerified) onVerified();
            } else {
                alert("Failed to manual approve: " + response.data.message);
            }
        } catch (err) {
            alert("Error: " + (err.response?.data?.message || err.message));
        }
    };

    const handleReject = async () => {
        if (!window.confirm("Are you sure you want to REJECT this admission?")) return;
        setLoading(true);
        try {
            const response = await api.post('/admissions/reject-manual', { admission_id: admissionId });
            if (response.data.status === 'success') {
                alert('Admission Rejected Successfully!');
                if (onVerified) onVerified();
            } else {
                alert("Failed to reject: " + response.data.message);
            }
        } catch (err) {
            alert("Error: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
            {isManual && (
                <button
                    onClick={handleReject}
                    disabled={loading}
                    style={{
                        flex: 1, padding: '0.8rem',
                        background: '#E53E3E',
                        color: 'white',
                        border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {loading ? 'Processing...' : <><MdWarning size={18} /> Reject</>}
                </button>
            )}
            <button
                onClick={handleCheck}
                disabled={loading}
                style={{
                    flex: 2, padding: '0.8rem',
                    background: isManual ? '#2F855A' : '#DD6B20', // Green for Manual Approve, Orange for Verify
                    color: 'white',
                    border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                    whiteSpace: 'nowrap'
                }}
            >
                {loading ? 'Processing...' : (
                    isManual ? <><MdCheckCircle size={18} /> Approve & Confirm</> : <><MdRestore size={18} /> Verify Payment Status</>
                )}
            </button>
        </div>
    );
};

